import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    IconButton
} from '@mui/material';
import {
    Download,
    Refresh
} from '@mui/icons-material';
import { getAssessmentResults, getAssignments, getUsers } from '../Queries';
import { getFilterProgramCodes } from '../../utils/clientLogoService';
import { getAllClients, getDropdownData } from '../Queries';
import PrintDashboard from './DashboardPrint';
import PrintIndividualAssessment from './PrintIndividualAssessment';
import SummaryCards from './SummaryCards';
import AssessmentScoreCharts from './AssessmentScoreCharts';
import PerformanceDistribution from './PerformanceDistribution';
import DetailedUserResults from './DetailedUserResults';
import EmptyState from './EmptyState';
import LoadingScreen from '../UI/LoadingScreen';
import TabsLayout from '../Common/TabsLayout';

// Performance level colors and configurations
export const performanceConfig = {
    "💎 Exceptional": { color: "#4caf50", bgColor: "#e8f5e8", textColor: "#2e7d32" },
    "🌟 Proficient": { color: "#2196f3", bgColor: "#e3f2fd", textColor: "#1565c0" },
    "⚙️ Developing": { color: "#ff9800", bgColor: "#fff3e0", textColor: "#ef6c00" },
    "🛠 Needs Improvement": { color: "#f44336", bgColor: "#ffebee", textColor: "#c62828" },
    "🔴 Unsatisfactory": { color: "#9c27b0", bgColor: "#f3e5f5", textColor: "#7b1fa2" }
};

// Enhanced useReactToPrint implementation
const useReactToPrint = (options) => {
    return () => {
        if (options.content) {
            const printWindow = window.open('', '_blank');
            let printContent = options.content().innerHTML;

            printWindow.document.write(`
                <html>
                    <head>
                        <title>${options.documentTitle || 'Assessment Report'}</title>
                        <style>
                            @media print {
                                * {
                                    -webkit-print-color-adjust: exact !important;
                                    color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }
                                
                                @page {
                                    margin: 80px 40px 60px 40px;
                                    size: A4;
                                    @bottom-center {
                                        content: element(footer);
                                    }
                                }
                                
                                body {
                                    margin: 0;
                                    padding: 0;
                                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                                    line-height: 1.4;
                                    color: #333 !important;
                                    background: white !important;
                                }
                                
                                /* Print header using table-header-group to repeat on all pages */
                                .print-header {
                                    display: table-header-group !important;
                                    position: static !important;
                                    width: 100% !important;
                                    background: white !important;
                                    border-bottom: 2px solid #0a0068 !important;
                                    padding: 15px 20px 25px 20px !important;
                                    margin-bottom: 20px !important;
                                    box-sizing: border-box !important;
                                    page-break-inside: avoid !important;
                                    break-inside: avoid !important;
                                }
                                
                                /* Print footer positioned at bottom of each page */
                                .print-footer {
                                    position: running(footer) !important;
                                    width: 100% !important;
                                    background: white !important;
                                    border-top: 2px solid #0a0068 !important;
                                    padding: 15px 20px 10px 20px !important;
                                    font-size: 10px !important;
                                    color: #666 !important;
                                    text-align: center !important;
                                    box-sizing: border-box !important;
                                }
                                
                                /* Fallback for browsers that don't support running elements */
                                @supports not (position: running(footer)) {
                                    .print-footer {
                                        position: fixed !important;
                                        bottom: 0 !important;
                                        left: 0 !important;
                                        right: 0 !important;
                                        z-index: 1000 !important;
                                    }
                                    
                                    body {
                                        padding-bottom: 80px !important;
                                    }
                                }
                                
                                /* Main container as table to enable header/footer groups */
                                .print-wrapper {
                                    display: table !important;
                                    width: 100% !important;
                                    table-layout: fixed !important;
                                }
                                
                                /* Content area */
                                .print-content {
                                    display: table-row-group !important;
                                    padding: 30px 20px 20px 20px !important;
                                }
                                
                                .page-break {
                                    page-break-before: always;
                                }
                                
                                .no-break, .assessment-card {
                                    page-break-inside: avoid;
                                    break-inside: avoid;
                                }
                                
                                h1, h2, h3, h4, h5, h6 {
                                    color: #0a0068 !important;
                                    page-break-after: avoid;
                                    break-after: avoid;
                                    orphans: 3;
                                    widows: 3;
                                }
                                
                                p, div, span {
                                    orphans: 2;
                                    widows: 2;
                                }
                                
                                /* Add small breaks before major sections */
                                .major-section {
                                    margin-top: 30px;
                                }
                            }
                            
                            /* Screen styles */
                            body {
                                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                                line-height: 1.4;
                                color: #333;
                                background: white;
                            }
                            
                            h1, h2, h3 { 
                                color: #0a0068 !important; 
                            }
                            
                            table { 
                                border-collapse: collapse; 
                            }
                            
                            th, td { 
                                border: 1px solid #ddd; 
                            }
                        </style>
                    </head>
                    <body>${printContent}</body>
                </html>
            `);
            printWindow.document.close();

            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        }
    };
};

// Main Dashboard Component
function Dashboard() {
    const [selectedTab, setSelectedTab] = useState(0);
    const [assessmentData, setAssessments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [usersByProgram, setUsersByProgram] = useState({});
    const printRef = useRef();
    const individualPrintRef = useRef();
    const [individualAssessment, setIndividualAssessment] = useState(null);
    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    const getAssessmentData = async () => {
        try {
            setIsLoading(true);
            const assignmentsData = await getAssignments();
            const usersData = await getUsers();
            setAllUsers(usersData);

            // Pre-calculate user counts by program code to avoid re-fetching
            const programUserCounts = {};
            usersData.forEach(user => {
                if (user.role === "User" && user.programCode) {
                    const codes = Array.isArray(user.programCode)
                        ? user.programCode
                        : user.programCode.split(',').map(c => c.trim());

                    codes.forEach(code => {
                        if (!programUserCounts[code]) {
                            programUserCounts[code] = 0;
                        }
                        programUserCounts[code]++;
                    });
                }
            });
            setUsersByProgram(programUserCounts);

            // Filter and process assessments sequentially
            const processedAssignments = [];

            for (const assignmentItem of assignmentsData) {
                if (assignmentItem.assignment_topic === "assessment") {
                    try {
                        const allAssessmentResults = await getAssessmentResults(assignmentItem.form_id);
                        // Attach the results to the assignment object
                        assignmentItem.assessmentResults = allAssessmentResults || [];
                        assignmentItem.programCodes = assignmentItem.program_code ?
                            assignmentItem.program_code.split(',').map(code => code.trim()) : [];
                        processedAssignments.push(assignmentItem);
                    } catch (error) {
                        console.error(`Error fetching results for form_id ${assignmentItem.form_id}:`, error);
                        // Still add the assignment but with empty results
                        assignmentItem.assessmentResults = [];
                        assignmentItem.programCodes = assignmentItem.program_code ?
                            assignmentItem.program_code.split(',').map(code => code.trim()) : [];
                        processedAssignments.push(assignmentItem);
                    }
                }
            }
            setAssessments(processedAssignments);
        } catch (error) {
            console.error('An error occurred while fetching assignments data.', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAssessmentData();
    }, []);

    // State for storing client hierarchy data
    const [clientHierarchy, setClientHierarchy] = useState([]);
    const [userFilteredProgramCodes, setUserFilteredProgramCodes] = useState([]);
    const [clientFilteredProgramCodes, setClientFilteredProgramCodes] = useState([]);

    // Memoize assessment data length to prevent unnecessary re-runs
    const hasAssessmentData = useMemo(() => assessmentData.length > 0, [assessmentData.length]);

    // Build client-batch hierarchy
    useEffect(() => {
        const buildClientHierarchy = async () => {
            if (!hasAssessmentData) return;

            try {
                const [clients, dropdown] = await Promise.all([
                    getAllClients(),
                    getDropdownData()
                ]);
                
                // Get all unique program codes from assessments
                const allProgramCodes = new Set();
                assessmentData.forEach(assessment => {
                    if (assessment.programCodes && Array.isArray(assessment.programCodes)) {
                        assessment.programCodes.forEach(code => {
                            if (code && code.trim()) {
                                allProgramCodes.add(code.trim());
                            }
                        });
                    }
                });

                // Group program codes by client
                const clientGroups = new Map();
                const othersPrograms = [];

                for (const programCode of allProgramCodes) {
                    // Find the dropdown entry for this program code
                    const programEntry = dropdown.find(item => 
                        item.type === 'program_code' && 
                        item.name === programCode
                    );

                    if (programEntry?.client_code) {
                        // Find the client with matching client_code
                        const client = clients.find(client => 
                            client.client_code === programEntry.client_code
                        );

                        if (client) {
                            if (!clientGroups.has(client.client_code)) {
                                clientGroups.set(client.client_code, {
                                    clientCode: client.client_code,
                                    clientName: client.client_code,
                                    clientLogo: client.client_logo,
                                    description: client.description,
                                    programCodes: []
                                });
                            }
                            clientGroups.get(client.client_code).programCodes.push(programCode);
                        } else {
                            othersPrograms.push(programCode);
                        }
                    } else {
                        othersPrograms.push(programCode);
                    }
                }

                // Convert to array and add Others section if needed
                const hierarchy = Array.from(clientGroups.values());
                if (othersPrograms.length > 0) {
                    hierarchy.push({
                        clientCode: 'others',
                        clientName: 'Others',
                        clientLogo: null,
                        description: 'Programs not associated with any client',
                        programCodes: othersPrograms
                    });
                }

                setClientHierarchy(hierarchy);
            } catch (error) {
                console.error('Error building client hierarchy:', error);
                setClientHierarchy([]);
            }
        };

        buildClientHierarchy();
    }, [hasAssessmentData, assessmentData]);

    // Get filtered program codes for User role when assessments load
    useEffect(() => {
        const getUserFilteredCodes = async () => {
            if (userData?.user_role === 'User') {
                const userProgramCodes = userData.programCode || userData.user_batch;
                const filteredCodes = await getFilterProgramCodes(userProgramCodes);
                setUserFilteredProgramCodes(filteredCodes);
            }
        };

        if (hasAssessmentData) {
            getUserFilteredCodes();
        }
    }, [hasAssessmentData, userData?.user_role, userData?.programCode, userData?.user_batch]);

    // Get program codes for Client role when assessments load
    useEffect(() => {
        const getClientFilteredCodes = async () => {
            if (userData?.user_role === 'Client') {
                const clientProgramCodes = userData.programCode || userData.user_batch;

                // For clients, we want to show all their assigned program codes as separate tabs
                // Parse the program codes (could be comma-separated string or array)
                let programCodesArray = [];
                if (Array.isArray(clientProgramCodes)) {
                    programCodesArray = clientProgramCodes;
                } else if (typeof clientProgramCodes === 'string') {
                    programCodesArray = clientProgramCodes.split(',').map(code => code.trim()).filter(Boolean);
                } else if (clientProgramCodes) {
                    programCodesArray = [clientProgramCodes];
                }

                setClientFilteredProgramCodes(programCodesArray);
            } else {
                setClientFilteredProgramCodes([]);
            }
        };

        // Run immediately if userData is available, don't wait for assessmentData
        if (userData) {
            getClientFilteredCodes();
        }
    }, [userData?.user_role, userData?.programCode, userData?.user_batch]);

    // Get display tabs based on user role and client hierarchy
    const displayTabs = useMemo(() => {
        // For User role, use the client-aware filtered program codes (backwards compatibility)
        if (userData?.user_role === 'User') {
            return userFilteredProgramCodes.map(code => ({
                id: code,
                label: code,
                title: `${code} Dashboard`,
                description: `Assessment results and analytics for ${code} program`,
                type: 'program',
                code,
                programCode: code
            }));
        }

        // For Client users, show all their assigned program codes as separate tabs (backwards compatibility)
        if (userData?.user_role === 'Client') {
            const codes = clientFilteredProgramCodes.length > 0 ? clientFilteredProgramCodes : [userData?.user_batch].filter(Boolean);
            return codes.map(code => ({
                id: code,
                label: code,
                title: `${code} Dashboard`,
                description: `Assessment results and analytics for ${code} program`,
                type: 'program',
                code,
                programCode: code
            }));
        }

        // For Mentor and Admin users, show client hierarchy
        const tabs = [];
        clientHierarchy.forEach(client => {
            client.programCodes.forEach(programCode => {
                tabs.push({
                    id: `${client.clientCode}-${programCode}`,
                    label: client.clientCode === 'others' ? programCode : `${client.clientName} - ${programCode}`,
                    title: `${programCode} Dashboard`,
                    description: `Assessment results and analytics for ${programCode} program`,
                    type: 'client-program',
                    clientCode: client.clientCode,
                    clientName: client.clientName,
                    clientLogo: client.clientLogo,
                    programCode: programCode,
                });
            });
        });

        return tabs;
    }, [userData?.user_role, userFilteredProgramCodes, clientFilteredProgramCodes, clientHierarchy]);

    // Get unique program codes for backwards compatibility
    const allProgramCodes = useMemo(() => {
        return displayTabs.map(tab => tab.programCode || tab.code);
    }, [displayTabs]);

    // Filter data by selected program code
    const filteredData = useMemo(() => {
        if (allProgramCodes.length === 0) return assessmentData;

        const selectedProgramCode = allProgramCodes[selectedTab];
        if (!selectedProgramCode) return assessmentData;

        return assessmentData.map(assessment => ({
            ...assessment,
            assessmentResults: assessment.assessmentResults?.filter(assessmentItem => {
                const userBatch = assessmentItem.json?.user_details?.user_batch;
                return userBatch === selectedProgramCode;
            }) || []
        })).filter(assessment => assessment.assessmentResults.length > 0);
    }, [assessmentData, selectedTab, allProgramCodes]);

    // New tab change handler for TabsLayout
    const handleTabChange = (tabId) => {
        const tabIndex = displayTabs.findIndex(tab => tab.id === tabId);
        setSelectedTab(tabIndex >= 0 ? tabIndex : 0);
    };

    // Legacy tab change handler for backwards compatibility
    const handleLegacyTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    // Print function
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Assessment-Report-${allProgramCodes[selectedTab] || 'All-Programs'}-${new Date().toISOString().split('T')[0]}`
    });

    const generatePDFReport = () => {
        handlePrint();
    };

    // Individual assessment print function
    const handleIndividualPrint = useReactToPrint({
        content: () => individualPrintRef.current,
        documentTitle: `Individual-Assessment-Report-${individualAssessment?.assignment_name?.replace(/[^a-zA-Z0-9]/g, '-') || 'Assessment'}-${allProgramCodes[selectedTab] || 'All-Programs'}-${new Date().toISOString().split('T')[0]}`
    });

    const handleExportIndividualPDF = (assessment) => {
        setIndividualAssessment(assessment);
        // Use setTimeout to ensure the component is rendered before printing
        setTimeout(() => {
            handleIndividualPrint();
        }, 100);
    };

    // Calculate overall average score
    const overallAverage = useMemo(() => {
        if (filteredData.length === 0) return 0;

        let totalScore = 0;
        let totalParticipants = 0;

        filteredData.forEach(assessment => {
            if (assessment.assessmentResults && assessment.assessmentResults.length > 0) {
                assessment.assessmentResults.forEach(result => {
                    if (result.json && typeof result.json.finalScore === 'number') {
                        totalScore += result.json.finalScore;
                        totalParticipants++;
                    }
                });
            }
        });

        return totalParticipants > 0 ? Math.round(totalScore / totalParticipants) : 0;
    }, [filteredData]);

    // Get participants count for current tab from cached data
    const participantsEnrolled = useMemo(() => {
        if (allProgramCodes.length === 0 || selectedTab >= allProgramCodes.length) {
            return 0;
        }
        const programCode = allProgramCodes[selectedTab];
        return usersByProgram[programCode] || 0;
    }, [selectedTab, allProgramCodes, usersByProgram]);

    // Get current program code information for the selected tab
    const currentProgramInfo = useMemo(() => {
        if (selectedTab >= displayTabs.length) return null;
        
        const currentTab = displayTabs[selectedTab];
        const activeProgramCode = allProgramCodes[selectedTab];
        
        if (currentTab?.type === 'client-program') {
            // For client-program tabs, get client info but use batch description
            return {
                clientCode: currentTab.clientCode,
                clientName: currentTab.clientName,
                clientLogo: currentTab.clientLogo,
                programCode: currentTab.programCode,
                description: '' // Will be populated by getBatchDescription
            };
        } else if (activeProgramCode) {
            // For other tabs, simple program code info
            return {
                programCode: activeProgramCode,
                description: '' // Will be populated by getBatchDescription
            };
        }
        
        return null;
    }, [selectedTab, displayTabs, allProgramCodes]);

    // Get batch description for the current program code
    const [batchDescription, setBatchDescription] = useState('');
    
    useEffect(() => {
        const getBatchDescription = async () => {
            if (!currentProgramInfo?.programCode) {
                setBatchDescription('');
                return;
            }

            try {
                const response = await getDropdownData();
                
                // Find the batch/program code in dropdown data
                const programEntry = response.find(item => 
                    item.type === 'program_code' && 
                    item.name === currentProgramInfo.programCode
                );

                if (programEntry?.json_data?.description) {
                    setBatchDescription(programEntry.json_data.description);
                } else {
                    setBatchDescription('');
                }
            } catch (error) {
                console.error('Error fetching batch description:', error);
                setBatchDescription('');
            }
        };

        getBatchDescription();
    }, [currentProgramInfo?.programCode]);

    // Combined info for the report
    const currentReportInfo = useMemo(() => {
        if (!currentProgramInfo) return null;

        return {
            ...currentProgramInfo,
            description: batchDescription
        };
    }, [currentProgramInfo, batchDescription]);

    // Action buttons for the header
    const actionButtons = (
        <>
            {!isLoading && (
                <Button
                    startIcon={<Download />}
                    onClick={handlePrint}
                    variant="contained"
                    size="small"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        background: 'linear-gradient(45deg, #0a0068 30%, #1a1078 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #1a1078 30%, #0a0068 90%)'
                        }
                    }}
                >
                    Export PDF
                </Button>
            )}
            <IconButton
                onClick={getAssessmentData}
                size="small"
                sx={{
                    bgcolor: 'white',
                    border: '1px solid #e0e0e0',
                    '&:hover': { bgcolor: 'grey.100' }
                }}
            >
                <Refresh fontSize="small" />
            </IconButton>
        </>
    );

    // Render tab content for TabsLayout
    const renderTabContent = (activeTab) => {
        return (
            <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pt: 2 }}>
                {/* Summary Cards */}
                <SummaryCards
                    filteredData={filteredData}
                    participantsEnrolled={participantsEnrolled}
                    overallAverage={overallAverage}
                    allProgramCodes={allProgramCodes}
                    selectedTab={selectedTab}
                />

                {/* Assessment Score Charts */}
                <AssessmentScoreCharts
                    filteredData={filteredData}
                    allUsers={allUsers}
                    allProgramCodes={allProgramCodes}
                    selectedTab={selectedTab}
                    onExportIndividualPDF={handleExportIndividualPDF}
                />

                {/* Performance Distribution */}
                <PerformanceDistribution
                    filteredData={filteredData}
                    performanceConfig={performanceConfig}
                />

                {/* Detailed User Results */}
                <DetailedUserResults
                    filteredData={filteredData}
                    performanceConfig={performanceConfig}
                />

                {/* Empty State */}
                <EmptyState
                    filteredData={filteredData}
                    allProgramCodes={allProgramCodes}
                    selectedTab={selectedTab}
                />
            </Box>
        );
    };

    if (isLoading) {
        return <LoadingScreen message="Loading dashboard data..." />;
    }

    // If user has no tabs to display (single program), show content directly
    if (displayTabs.length === 1 || displayTabs.length === 0) {
        return (
            <Box sx={{ bgcolor: 'white', minHeight: '100vh' }}>
                {/* Hidden Printable Components */}
                <div style={{ display: 'none' }}>
                    <PrintDashboard
                        ref={printRef}
                        filteredData={filteredData}
                        allProgramCodes={allProgramCodes}
                        selectedTab={selectedTab}
                        participantsEnrolled={participantsEnrolled}
                        overallAverage={overallAverage}
                        performanceConfig={performanceConfig}
                        currentClientInfo={currentReportInfo}
                    />
                    {individualAssessment && (
                        <PrintIndividualAssessment
                            ref={individualPrintRef}
                            assessment={individualAssessment}
                            allProgramCodes={allProgramCodes}
                            selectedTab={selectedTab}
                            participantsEnrolled={participantsEnrolled}
                            performanceConfig={performanceConfig}
                        />
                    )}
                </div>

                {/* Header with title and action buttons */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Box sx={{ flex: 1 }}>
                        <Box component="h1" sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            color: '#000',
                            margin: 0,
                            mb: 0.5
                        }}>
                            {displayTabs[0]?.title || 'Assessment Dashboard'}
                        </Box>
                        <Box sx={{
                            fontFamily: 'Poppins',
                            color: '#9AA6AC',
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: 1.5
                        }}>
                            {displayTabs[0]?.description || 'Assessment results and analytics'}
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexShrink: 0
                    }}>
                        {actionButtons}
                    </Box>
                </Box>

                {/* Single tab content */}
                {renderTabContent(displayTabs[0]?.id)}
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: 'white', minHeight: '100vh' }}>
            {/* Hidden Printable Components */}
            <div style={{ display: 'none' }}>
                <PrintDashboard
                    ref={printRef}
                    filteredData={filteredData}
                    allProgramCodes={allProgramCodes}
                    selectedTab={selectedTab}
                    participantsEnrolled={participantsEnrolled}
                    overallAverage={overallAverage}
                    performanceConfig={performanceConfig}
                    currentClientInfo={currentReportInfo}
                />
                {individualAssessment && (
                    <PrintIndividualAssessment
                        ref={individualPrintRef}
                        assessment={individualAssessment}
                        allProgramCodes={allProgramCodes}
                        selectedTab={selectedTab}
                        participantsEnrolled={participantsEnrolled}
                        performanceConfig={performanceConfig}
                    />
                )}
            </div>

            <TabsLayout
                tabs={displayTabs}
                title="Assessment Dashboard"
                defaultTab={displayTabs[0]?.id}
                onTabChange={handleTabChange}
                actionButtons={actionButtons}
            >
                {renderTabContent}
            </TabsLayout>
        </Box>
    );
}

export default Dashboard;